/**
 * Event Transport Manager
 *
 * Manages transport assignments for calendar events.
 * Handles creation, update, and deletion of transport data.
 */

import type { TransportAssignment, TransportMode } from '@/types/transport';

/**
 * EventTransportManager
 *
 * Utility class for managing transport assignments.
 */
export class EventTransportManager {
  /**
   * Generate a unique ID for a transport assignment
   */
  static generateId(): string {
    return `transport_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Create a new transport assignment
   */
  static createAssignment(
    mode: TransportMode,
    options?: {
      vehicleName?: string;
      driverFamilyMemberId?: string;
      passengerFamilyMemberIds?: string[];
      notes?: string;
    }
  ): TransportAssignment {
    return {
      id: this.generateId(),
      mode,
      vehicleName: options?.vehicleName,
      driverFamilyMemberId: options?.driverFamilyMemberId,
      passengerFamilyMemberIds: options?.passengerFamilyMemberIds || [],
      notes: options?.notes,
    };
  }

  /**
   * Update an existing transport assignment
   */
  static updateAssignment(
    assignments: TransportAssignment[],
    assignmentId: string,
    updates: Partial<Omit<TransportAssignment, 'id'>>
  ): TransportAssignment[] {
    return assignments.map(assignment => {
      if (assignment.id === assignmentId) {
        return { ...assignment, ...updates };
      }
      return assignment;
    });
  }

  /**
   * Remove a transport assignment
   */
  static removeAssignment(
    assignments: TransportAssignment[],
    assignmentId: string
  ): TransportAssignment[] {
    return assignments.filter(a => a.id !== assignmentId);
  }

  /**
   * Get a summary of transport for an event
   * Returns passenger count and mode icons
   */
  static getSummary(assignments: TransportAssignment[]): {
    passengerCount: number;
    modes: TransportMode[];
  } {
    const passengerCount = assignments.reduce(
      (total, a) => total + a.passengerFamilyMemberIds.length,
      0
    );
    const modes = [...new Set(assignments.map(a => a.mode))];
    return { passengerCount, modes };
  }
}
